import { JSX } from 'cdui-js';
import { IOS_TAP_SLOP_PX } from '../../../../lib/iosTapSlop';

const ccolor = '#1060FF';

/** 触摸后抑制合成 click，避免 WebView 双次切换 */
let lastTouchActivateAt = 0;
const TOUCH_CLICK_GUARD_MS = 450;

export type CheckBoxProps = JSX.HTMLAttributes<never> & {
  type?: number;
  active: boolean;
  change: (e: boolean) => void;
  /** 文案区上下内边距，默认 8 */
  labelPaddingY?: number;
};

export const CheckBox = (props: CheckBoxProps) => {
  const ctype = () => (!props.type ? 1 : props.type);
  const labelPadY = `${props.labelPaddingY ?? 8}px`;

  const CheckIcon = () =>
    ctype() === 1 ? (
      <svg width="18" height="18" viewBox="0 0 18 18" stroke="none" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="16" height="16" rx="4" fill={ccolor} />
        <path
          d="M12 7L8.44 10.6485C8 11.1172 8 11.1172 7.50667 10.6485L6 9.21694"
          stroke="white"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5625" y="0.5625" width="12.875" height="12.875" rx="6.4375" fill={ccolor} />
        <rect x="0.5625" y="0.5625" width="12.875" height="12.875" rx="6.4375" stroke={ccolor} stroke-width="1.125" />
        <path
          d="M10 5L6.44 8.64846C6 9.11718 6 9.11718 5.50667 8.64846L4 7.21694"
          stroke="white"
          stroke-width="1.5"
          stroke-miterlimit="10"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    );

  const UncheckIcon = () =>
    ctype() === 1 ? (
      <svg width="18" height="18" viewBox="0 0 18 18" stroke="none" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1.5" y="1.5" width="15" height="15" rx="3.5" stroke="#E4E4E4" />
      </svg>
    ) : (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5625" y="0.5625" width="12.875" height="12.875" rx="6.4375" stroke="#E4E4E4" stroke-width="1.125" />
      </svg>
    );

  const isNavOnly = (e: Event) => !!(e.target as Element | null)?.closest?.('[data-nav-only]');

  const toggle = (e: Event) => {
    if (isNavOnly(e)) return;
    props.change(!props.active);
  };

  return (
    <div
      class={`flex align-center width-100 gap-8px ${props.class || ''}`.trim()}
      style={
        typeof props.style === 'object' && props.style
          ? {
              cursor: 'pointer',
              'user-select': 'none',
              '-webkit-tap-highlight-color': 'transparent',
              'touch-action': 'manipulation',
              ...props.style,
            }
          : {
              cursor: 'pointer',
              'user-select': 'none',
              '-webkit-tap-highlight-color': 'transparent',
              'touch-action': 'manipulation',
            }
      }
      role="checkbox"
      aria-checked={props.active}
      tabindex={-1}
      onclick={(e) => {
        if (Date.now() - lastTouchActivateAt < TOUCH_CLICK_GUARD_MS) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        toggle(e);
      }}
      ontouchstart={(e) => {
        const t = e.changedTouches?.[0] ?? e.touches?.[0];
        const el = e.currentTarget as HTMLElement | null;
        if (!t || !el) return;
        el.dataset.touchX = String(t.clientX);
        el.dataset.touchY = String(t.clientY);
      }}
      ontouchend={(e) => {
        if (isNavOnly(e)) return;
        const t = e.changedTouches?.[0];
        const el = e.currentTarget as HTMLElement | null;
        if (t && el?.dataset.touchX != null && el?.dataset.touchY != null) {
          const dx = Math.abs(t.clientX - Number(el.dataset.touchX));
          const dy = Math.abs(t.clientY - Number(el.dataset.touchY));
          if (dx > IOS_TAP_SLOP_PX || dy > IOS_TAP_SLOP_PX) return;
        }
        lastTouchActivateAt = Date.now();
        e.preventDefault();
        toggle(e);
      }}
    >
      {/* 视觉 18×18；padding 扩到约 44×44，负 margin 不撑开行高 */}
      <div
        class="flex align-center justify-center flex-shrink-0"
        data-tap-activate=""
        style={{
          width: '18px',
          height: '18px',
          padding: '13px',
          margin: '-13px',
          'box-sizing': 'content-box',
        }}
        aria-hidden="true"
      >
        {props.active ? <CheckIcon /> : <UncheckIcon />}
      </div>

      {/* flex-1 + min-width:0：覆盖 base.css 的 `* { flex: none }`，避免长文撑开横向滚动 */}
      <div
        class="flex-1"
        style={{
          'min-width': 0,
          'max-width': '100%',
          'white-space': 'normal',
          'padding-top': labelPadY,
          'padding-bottom': labelPadY,
        }}
      >
        {props.children}
      </div>
    </div>
  );
};
